import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { useState, type ReactNode } from "react";

export interface RepoFileTreeEntry {
  path: string;
  size: number;
}

export interface RepoFileTreeProps {
  ariaLabel: string;
  emptyMessage?: string;
  files: readonly RepoFileTreeEntry[];
  onSelect: (path: string) => void;
  selectedPath: string | null;
}

type RepoTreeNode = {
  folders: Map<string, RepoTreeNode>;
  files: RepoFileTreeEntry[];
};

function emptyNode(): RepoTreeNode {
  return { folders: new Map(), files: [] };
}

function insertEntry(root: RepoTreeNode, entry: RepoFileTreeEntry): void {
  const segments = entry.path.split("/");
  let node = root;
  for (const folder of segments.slice(0, -1)) {
    let child = node.folders.get(folder);
    if (!child) {
      child = emptyNode();
      node.folders.set(folder, child);
    }
    node = child;
  }
  node.files.push(entry);
}

/**
 * Accessible repository navigation: collapsible folders over the server's
 * path-sorted listing. Presentation-only; selection and edits stay with the
 * caller.
 */
export function RepoFileTree({
  ariaLabel,
  emptyMessage = "This repository has no files yet.",
  files,
  onSelect,
  selectedPath,
}: RepoFileTreeProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  if (files.length === 0) {
    return (
      <nav aria-label={ariaLabel} className="text-sm text-[var(--muted-foreground)]">
        {emptyMessage}
      </nav>
    );
  }

  const root = emptyNode();
  for (const file of files) insertEntry(root, file);

  const toggleFolder = (path: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const renderNode = (node: RepoTreeNode, prefix: string): ReactNode => (
    <ul className="grid gap-1">
      {Array.from(node.folders, ([name, child]) => {
        const path = prefix ? `${prefix}/${name}` : name;
        const open = !collapsed.has(path);
        return (
          <li key={`folder:${path}`}>
            <Button
              aria-expanded={open}
              aria-label={`Folder ${path}`}
              className="w-full justify-start truncate font-mono"
              onClick={() => toggleFolder(path)}
              size="sm"
              type="button"
              variant="ghost"
            >
              {name}/
            </Button>
            {open ? <div className="pl-4">{renderNode(child, path)}</div> : null}
          </li>
        );
      })}
      {node.files.map((file) => {
        const selected = file.path === selectedPath;
        return (
          <li key={`file:${file.path}`}>
            <Button
              aria-current={selected ? "page" : undefined}
              aria-label={file.path}
              aria-pressed={selected}
              className="w-full justify-start truncate font-mono"
              onClick={() => onSelect(file.path)}
              size="sm"
              type="button"
              variant={selected ? "secondary" : "ghost"}
            >
              {file.path.split("/").at(-1)}
            </Button>
          </li>
        );
      })}
    </ul>
  );

  return <nav aria-label={ariaLabel}>{renderNode(root, "")}</nav>;
}
